'use client';
import { useMemo, useState } from 'react';
import {
  useProduct,
  useSelectedVariant,
  useBundleSelection
} from '@/components/product/product-context';
import { useCart } from '@/components/commerce/cart/cart-context';
import { useRouter } from 'next/navigation';
import { mergeBundleGwpItemsIntoList } from './bundle-gwp-helpers';
import { elevarAddToCart } from '@/lib/elevar/cart-events';
import { gtmAddToCart } from '@/utils/events/gtmEvents';
import AddToCartToastBody from '@/components/toast/toast-bodies/AddToCartToastBody';
import { useToast } from '@/components/toast/ToastProvider';
const getBundleItemsToAdd = (bundleState, currentVariant) => {
  const firstOptionValue = currentVariant.selectedOptions[0].value;
  return bundleState.currentOffers
    .map((offer) => {
      if (!offer.product || !offer.variant) {
        console.warn('Invalid offer detected:', offer);
        return null;
      }
      const compareAtDifference =
        parseInt(offer.variant.compareAtPrice.amount) -
        parseInt(offer.variant.price.amount);
      const baseBundleId = bundleState.bundleId;
      const mattressHandle =
        bundleState?.selectedProduct?.shopifyProduct?.handle;
      const uniqueBundleId = `${baseBundleId}-${mattressHandle}`;
      return {
        variant: offer.variant,
        product: offer.product,
        quantity: 1,
        customAttributes: [
          {
            key: '_productType',
            value: offer.product.productType
          },
          {
            key: '_productTitle',
            value: offer.product.title
          },
          {
            key: '_productImage',
            value: offer.product.featuredImage?.url
          },
          {
            key: '_compareAtPrice',
            value: offer.variant.compareAtPrice?.amount
          },
          {
            key: 'Bundle',
            value: bundleState.bundleName
          },
          { key: '_bundleVariant', value: firstOptionValue },
          { key: '_bundleHandle', value: bundleState.details?.url },
          {
            key: '_bundleId',
            value: uniqueBundleId
          },
          {
            key: '_bundleSettingsProductId',
            value: bundleState.details?.bundleSettings?.id
          },
          {
            key: '_gwpParentProduct',
            value: JSON.stringify(offer.settings?.gwp_parent_product)
          },
          {
            key: '_bundleDiscountAmount',
            value: offer.discount
          },
          {
            key: '_originalBundleProductId',
            value: offer.original_bundle_product_id?.toString()
          },
          {
            key: '_discount_amount',
            value: (compareAtDifference * 100).toString()
          }
        ]
      };
    })
    .filter(Boolean);
};
/**
 * An add to cart button for bundles.
 * The file this is in is called simply AtcButton, which is misleading but I
 * haven't changed it for now because that's what it was called in leesa-next.
 */
export default function BundleAtcButton({ block, position }) {
  const { _metadata, button_text } = block.atc_button;
  const { addCartItems } = useCart();
  const { settings, productData } = useProduct();
  const { selectedOptions } = useSelectedVariant();
  const bundleState = useBundleSelection();
  const { addToast } = useToast();
  // If bundleState selectedProduct?.shopifyProduct is not available, we fallback to productData. It should always be available in the context since we are setting it from the bundle options default option
  const product =
    bundleState?.selectedProduct?.shopifyProduct || productData?.shopifyProduct;
  const { variants } = product;
  const router = useRouter();
  const currentOffers = bundleState.currentOffers;
  const currentVariant = variants.find(
    (v) => v.selectedOptions[0]?.value === selectedOptions?.size
  );

  // Check if any bundle offer items are out of stock
  const bundleHasOutOfStockItems = useMemo(() => {
    if (!currentOffers) return false;

    return currentOffers.some((offer) => {
      // Check if the product itself is marked as out of stock
      if (offer.product?.outOfStock?.value === 'true') {
        return true;
      }

      // Check if the specific variant is out of stock
      if (offer.variant?.outOfStock?.value === 'true') {
        return true;
      }

      // Check product-level metafields for out of stock status
      const productOutOfStockMetafield = offer.product?.metafields?.find(
        (metafield) => metafield?.key === 'product_out_of_stock'
      );

      if (productOutOfStockMetafield?.value?.toLowerCase() === 'true') {
        return true;
      }

      // Check variant-level metafields for out of stock status
      const variantOutOfStockMetafield = offer.variant?.metafields?.find(
        (metafield) => metafield?.key === 'variant_out_of_stock'
      );

      if (variantOutOfStockMetafield?.value?.toLowerCase() === 'true') {
        return true;
      }

      return false;
    });
  }, [currentOffers]);

  const itemsToAdd = useMemo(() => {
    const items = getBundleItemsToAdd(bundleState, currentVariant);
    // First convert the type to something mergeGwpItemsIntoList can use
    // Merge the GWP items into the list of items to add to cart
    return mergeBundleGwpItemsIntoList(
      items,
      bundleState.details.gwpOptionManager,
      currentVariant.selectedOptions[0].value,
      bundleState.bundleName,
      bundleState.bundleId
    );
  }, [bundleState, currentVariant]);
  const initialState = {
    name: 'Add',
    copy: button_text,
    class: 'btn-primary',
    disabled: false
  };
  const [buttonState, setButtonState] = useState(initialState);
  const handleSubmit = async (event) => {
    event.preventDefault();
    const loadingState = {
      name: 'Adding',
      copy: 'Adding',
      class: 'btn-primary opacity-60',
      disabled: true
    };
    setButtonState(loadingState);
    try {
      const { error, cart: updatedCart } = await addCartItems(itemsToAdd, {
        suppressSuccessToast: true
      });

      setButtonState(initialState);

      // The cart context already showed the error toast, so just stay put.
      if (error) {
        return;
      }

      // Handle the success toast ourselves as we need to customize it for
      // bundles. Set up a "product" object with the bundle name for the toast.
      const almostProduct = {
        title: bundleState.details.pricingMetafield.name
      };
      addToast(
        <AddToCartToastBody
          product={almostProduct}
          variant={currentVariant}
          additionalProducts={[]}
          gwp={itemsToAdd.filter((item) =>
            item.customAttributes?.find(
              (attribute) => attribute?.key === '_isPromo'
            )
          )}
          checkoutUrl={updatedCart?.checkoutUrl}
        />,
        { type: 'positive', timer: 4000 }
      );
      elevarAddToCart(itemsToAdd);
      gtmAddToCart({ itemsToAdd: itemsToAdd, page: 'Page' });
      router.push('/cart');
    } catch (error) {
      console.error('Error adding items to cart:', error);
      setButtonState(initialState);
    }
  };
  return (
    <section
      id={`${_metadata.uid}_${position}`}
      data-name="atc-button"
      className="not-last:mb-8"
    >
      <form onSubmit={handleSubmit}>
        <SubmitButton
          shopifyProduct={product}
          selectedVariantId={currentVariant.id}
          buttonState={buttonState}
          settings={settings}
          bundleState={bundleState}
          bundleHasOutOfStockItems={bundleHasOutOfStockItems}
        />
      </form>
    </section>
  );
}
function SubmitButton({
  selectedVariantId,
  buttonState,
  shopifyProduct,
  bundleHasOutOfStockItems,
  bundleState
}) {
  const variantSize = shopifyProduct.variants.find(
    (variant) => variant.id === selectedVariantId
  )?.title;
  const ariaLabel = `Add ${variantSize} size bundle to cart`;

  // Check bundle's out of stock status from Shopify metafields
  const bundleSettings = bundleState?.details?.bundleSettings;
  const bundleOutOfStock = bundleSettings?.outOfStock?.value === 'true';

  const isOutOfStock = bundleHasOutOfStockItems || bundleOutOfStock;

  if (isOutOfStock) {
    return (
      <button
        id="AddToCart"
        className="btn btn-disabled inline-block w-full"
        aria-label="Sorry, this product is out of stock"
        disabled={true}
      >
        Out of stock
      </button>
    );
  } else {
    return (
      <button
        id="AddToCart"
        type="submit"
        name={buttonState.name}
        className={`btn ${buttonState.class} inline-block w-full`}
        aria-label={ariaLabel}
        disabled={buttonState.disabled}
        data-cy="add-to-cart"
      >
        {buttonState.copy}
      </button>
    );
  }
}
